import { API_ENDPOINTS, buildApiUrl } from './apiConfig'

const STORAGE_KEYS = {
  token: 'digitalhugs_token',
  refreshToken: 'digitalhugs_refreshtoken',
  username: 'digitalhugs_username',
  userDetails: 'digitalhugs_user_details',
}

const safeStorageGet = (key) => {
  try {
    return localStorage.getItem(key)
  } catch (_) {
    return null
  }
}

export const AUTH_CHANGE_EVENT = 'digitalhugs-auth-changed'

const notifyAuthChanged = () => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(AUTH_CHANGE_EVENT))
  }
}

const safeStorageSet = (key, value) => {
  try {
    localStorage.setItem(key, value)
  } catch (_) {
    // Ignore storage write failures to avoid breaking login flow.
  }
}

const safeStorageRemove = (key) => {
  try {
    localStorage.removeItem(key)
  } catch (_) {
    // Ignore storage clear failures.
  }
}

const mapUserDetails = (data) => {
  if (!(data && data.status === true && data.userdata && data.officedata)) {
    return null
  }

  const { userdata, officedata } = data

  return {
    userdata,
    officedata,
    status: data.status,
    office_token: officedata?.token || officedata?.office_token || '',
    office_public_token: officedata?.office_public_token || '',
    brand_url: officedata?.brand_url || '',
    brand_domain: officedata?.brand_domain || '',
    firstName: userdata?.first_name || '',
    lastName: userdata?.last_name || '',
    role: userdata?.role,
    slug: officedata?.slug,
    officeName: officedata?.name,
    currencySymbol: officedata?.currency_symbol || '₹',
    logo: officedata?.logo,
    logoUrl: officedata?.logo_url || '',
    officeId: officedata?.id || '',
    plan: officedata?.plan || '',
    planDetails: officedata?.plan_details || '',
    planExpiry: officedata?.plan_expiry || '',
    planStatus: officedata?.plan_status || '',
    faceSearch: officedata?.face_search || '',
    featureIds: officedata?.feature_ids,
    permissions: userdata?.permissions || [],
    activeInvitations: data.active_invitations,
    activeAlbums: data.active_albums,
    activeSelectionAlbums: data.active_selection_albums,
    activeAiSharing: data.ai_sharing,
    remainingInvitations: data.remaining_invitations,
    remainingAlbums: data.remaining_albums,
    remainingSelectionAlbums: data.remaining_selection_albums,
    storageTotalKb: data.storage_total_kb,
    storageFormatted: data.storage_formatted,
    leadCounts: data.lead_counts,
    quotationCounts: data.quotation_counts,
    workActive: data.work_active,
    workCompleted: data.work_completed,
    portfolioViewsToday: userdata?.portfolio_views_today ?? data.portfolio_views_today ?? 0,
    portfolioViewsTotal: userdata?.portfolio_views_total ?? data.portfolio_views_total ?? 0,
  }
}

export const isAuthenticated = () => Boolean(safeStorageGet(STORAGE_KEYS.token))

export const getAuthToken = () => safeStorageGet(STORAGE_KEYS.token) || ''

export const getStoredUserDetails = () => {
  try {
    return JSON.parse(safeStorageGet(STORAGE_KEYS.userDetails) || '{}')
  } catch (_) {
    return {}
  }
}

const normalizeStoredList = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => String(item).trim()).filter(Boolean)
  }

  const raw = String(value || '').trim()
  if (!raw) {
    return []
  }

  if (raw.startsWith('[')) {
    try {
      const parsed = JSON.parse(raw)
      return Array.isArray(parsed)
        ? parsed.map((item) => String(item).trim()).filter(Boolean)
        : []
    } catch (_) {
      return []
    }
  }

  return raw
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean)
}

export const getStoredPermissions = () => {
  const userDetails = getStoredUserDetails()
  return normalizeStoredList(userDetails?.permissions ?? userDetails?.userdata?.permissions)
}

export const getStoredFeatureIds = () => {
  const userDetails = getStoredUserDetails()
  return normalizeStoredList(
    userDetails?.featureIds ??
      userDetails?.feature_ids ??
      userDetails?.officedata?.feature_ids ??
      userDetails?.features,
  )
}

export const hasStoredPermission = (permission) => {
  const permissions = getStoredPermissions()
  if (permissions.length === 0) {
    return true
  }

  return permissions.includes(String(permission || '').trim())
}

export const hasStoredFeature = (featureId) => {
  const featureIds = getStoredFeatureIds()
  if (featureIds.length === 0) {
    return true
  }

  return featureIds.includes(String(featureId || '').trim())
}

export const getStoredCurrencySymbol = () => {
  const userDetails = getStoredUserDetails()
  const symbol = String(
    userDetails?.currencySymbol ?? userDetails?.officedata?.currency_symbol ?? '',
  ).trim()

  return symbol || '₹'
}

export const getStoredOfficeToken = () => {
  const userDetails = getStoredUserDetails()
  return String(
    userDetails?.office_token ??
      userDetails?.officeToken ??
      userDetails?.office_public_token ??
      userDetails?.officePublicToken ??
      userDetails?.officedata?.office_token ??
      userDetails?.officedata?.officePublicToken ??
      userDetails?.officedata?.office_public_token ??
      userDetails?.officedata?.token ??
      userDetails?.lead_token ??
      userDetails?.leadToken ??
      userDetails?.token ??
      '',
  ).trim()
}

export const getStoredBrandUrl = () => {
  const userDetails = getStoredUserDetails()
  const raw = String(
    userDetails?.brand_url ??
      userDetails?.brandUrl ??
      userDetails?.brand_domain ??
      userDetails?.brandDomain ??
      userDetails?.brand ??
      userDetails?.officedata?.brand_url ??
      userDetails?.officedata?.brand_domain ??
      '',
  ).trim()

  if (!raw) {
    return 'https://digitalhugs.in'
  }

  return /^https?:\/\//i.test(raw) ? raw.replace(/^http:\/\//i, 'https://') : `https://${raw}`
}

// Same rule as Electron's plan-validator.js: the plan must be active (status 1 or 3) and its
// expiry date must not be before today. Expired accounts can still log in, but are sent to
// the renewal page until they renew.
export const isPlanValid = () => {
  const userDetails = getStoredUserDetails()
  const planStatus = String(userDetails?.planStatus ?? '')
  if (planStatus !== '1' && planStatus !== '3') {
    return false
  }

  const planExpiry = String(userDetails?.planExpiry || '').trim()
  if (!planExpiry) {
    return false
  }

  const expiryDate = new Date(planExpiry)
  if (Number.isNaN(expiryDate.getTime())) {
    return false
  }

  const today = new Date()
  today.setHours(0, 0, 0, 0)
  expiryDate.setHours(0, 0, 0, 0)
  return expiryDate >= today
}

export const getStoredUsername = () => safeStorageGet(STORAGE_KEYS.username) || ''

export const clearAuthStorage = () => {
  safeStorageRemove(STORAGE_KEYS.token)
  safeStorageRemove(STORAGE_KEYS.refreshToken)
  safeStorageRemove(STORAGE_KEYS.userDetails)
  // Legacy key some older builds wrote directly; nothing writes it anymore but a
  // stale value can still linger in a browser that logged in before that changed.
  safeStorageRemove('brand_url')

  try {
    sessionStorage.clear()
  } catch (_) {
    // Ignore storage clear failures.
  }

  // Wipe the service worker's "images" runtime cache (see service-worker.js) so a
  // shared/kiosk device doesn't keep the previous user's photos around after logout.
  // The precache (app shell JS/CSS) is left alone — it holds no user data.
  if (typeof caches !== 'undefined') {
    caches.delete('images').catch(() => {})
  }

  notifyAuthChanged()
}

export const persistAuthSession = ({ username, loginData, userData }) => {
  if (loginData?.token) {
    safeStorageSet(STORAGE_KEYS.token, loginData.token)
  }

  const refreshToken = loginData?.refreshtoken || loginData?.refresh_token
  if (refreshToken) {
    safeStorageSet(STORAGE_KEYS.refreshToken, refreshToken)
  }

  if (username) {
    safeStorageSet(STORAGE_KEYS.username, username)
  }

  const mappedUserDetails = mapUserDetails(userData)
  if (mappedUserDetails) {
    safeStorageSet(STORAGE_KEYS.userDetails, JSON.stringify(mappedUserDetails))
  }

  notifyAuthChanged()
  return mappedUserDetails
}

export const loginRequest = async ({ username, password }) => {
  const response = await fetch(buildApiUrl(API_ENDPOINTS.auth.login), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      user_name: username,
      password,
    }),
  })

  const data = await response.json()
  return {
    ok: response.ok,
    data,
  }
}

export const fetchUserData = async (token) => {
  const response = await fetch(buildApiUrl(API_ENDPOINTS.auth.data), {
    method: 'GET',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })

  const data = await response.json()
  return {
    ok: response.ok,
    data,
  }
}

export const requestPasswordResetEmail = async ({ email }) => {
  const response = await fetch(buildApiUrl(API_ENDPOINTS.auth.requestPasswordReset), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email }),
  })

  const data = await response.json()
  return {
    ok: response.ok,
    data,
  }
}

export const resetPasswordWithToken = async ({ token, password, passwordRepeat }) => {
  const response = await fetch(buildApiUrl(API_ENDPOINTS.auth.resetPassword), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      token,
      password,
      password_repeat: passwordRepeat,
    }),
  })

  const data = await response.json()
  return {
    ok: response.ok,
    data,
  }
}
